import productsApi from '@/services/products.service'
import type {
    ProductCreateModel,
    ProductModel,
    ProductUpdateModel
} from '@/types/models/ProductModel'
import {
    EHandler,
    EPaginationOptions,
    type PaginationDataType,
    type PaginationType
} from '@rfc/rfc-components-library/types'
import { getPageSize } from '@rfc/rfc-components-library/utils'
import { ref, type Ref } from 'vue'
import type { Commit, Dispatch } from 'vuex'
import { EProductActions, EProductMutations, type ProductState } from './ProductStoreTypes'
import { EAlertActions, EStoreGlobalModules } from '@rfc/rfc-components-library/store'
import i18n from '../../locales/i18n'

const state: Ref<PaginationDataType<ProductState>> = ref({
    data: {
        products: []
    },

    current_page: 1,
    last_page: 0,
    total: 0,
    to: 0,
    from: 0,
    per_page: getPageSize() ?? EPaginationOptions.DEFAULT,

    loading: false
})

const getters = {
    getproducts: () => state.value.data?.product,
    loading: () => state.value.loading
}

const actions = {
    async [EProductActions.FETCH_PRODUCTS](
        { commit }: { commit: Commit },
        params?: Partial<PaginationType>
    ) {
        commit(EProductMutations.SET_LOADING, true)
        return productsApi
            .getProducts(params)
            .then(data => {
                commit(EProductMutations.SET_PRODUCT, data.data)
                commit(EProductMutations.SET_PAGINATION, data)
                return data
            })
            .finally(() => {
                commit(EProductMutations.SET_LOADING, false)
            })
    },

    async [EProductActions.FETCH_PRODUCT]({ commit }: { commit: Commit }, { id }: { id: number }) {
        commit(EProductMutations.SET_LOADING, true)
        return await productsApi
            .getProduct(id)
            .then(res => {
                commit(EProductMutations.SET_PRODUCT, res.data)
                return res.data
            })
            .finally(() => commit(EProductMutations.SET_LOADING, false))
    },

    async [EProductActions.CREATE_PRODUCT](
        { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
        data: ProductCreateModel
    ) {
        commit(EProductMutations.SET_LOADING, true)
        return productsApi
            .createProduct(data)
            .then(res => {
                dispatch(EHandler.HANDLE_SUCCESS, {
                    name: data.name,
                    key: EProductActions.CREATE_PRODUCT
                })
                return res.data
            })
            .finally(() => commit(EProductMutations.SET_LOADING, false))
    },

    async [EProductActions.UPDATE_PRODUCT](
        { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
        { id, data }: { id: number; data: ProductUpdateModel }
    ) {
        commit(EProductMutations.SET_LOADING, true)
        return productsApi
            .updateProduct(id, data)
            .then(() =>
                dispatch(EHandler.HANDLE_SUCCESS, {
                    name: data.name,
                    key: EProductActions.UPDATE_PRODUCT
                })
            )
            .finally(() => {
                commit(EProductMutations.SET_LOADING, false)
            })
    },

    async [EProductActions.DELETE_PRODUCT](
        { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
        { id, name }: { id: number; name: string }
    ) {
        await productsApi.deleteProduct(id)
        commit(EProductMutations.REMOVE_PRODUCT, id)
        return productsApi
            .deleteProduct(id)
            .then(() =>
                dispatch(EHandler.HANDLE_SUCCESS, {
                    name,
                    key: EProductActions.DELETE_PRODUCT
                })
            )
            .finally(() => commit(EProductMutations.SET_LOADING, false))
    },

    [EProductActions.RESET_PRODUCT]({ commit }: { commit: Commit }) {
        commit(EProductMutations.RESET_PRODUCT)
    },

    [EHandler.HANDLE_SUCCESS](
        { dispatch }: { dispatch: Dispatch },
        { name, key }: { name: string; key: string }
    ) {
        dispatch(
            `${EStoreGlobalModules.ALERT}/${EAlertActions.QUEUE_ITEM}`,
            {
                action: EAlertActions.SUCCESS,
                message: i18n.global.t(`products.api.success.${key}`, { name })
            },
            { root: true }
        )
    }
}

const mutations = {
    [EProductMutations.SET_PRODUCTS](_: ProductState, products: ProductModel[]) {
        state.value.data!.products = products
    },
    [EProductMutations.SET_PRODUCT](_: ProductState, product: ProductModel) {
        state.value.data!.product = product
    },
    [EProductMutations.SET_LOADING](_: ProductState, loading: boolean) {
        state.value.loading = loading
    },
    [EProductMutations.SET_PAGINATION](_: ProductState, data: PaginationDataType<ProductModel>) {
        state.value.current_page = data.current_page
        state.value.total = data.total
        state.value.last_page = data.last_page
        state.value.to = data.to
        state.value.from = data.from
    },
    [EProductMutations.REMOVE_PRODUCT](_: ProductState, id: number) {
        state.value.data!.products =
            state.value.data?.products?.filter(item => item.id !== id) ?? []
    },
    [EProductMutations.RESET_PRODUCT]() {
        state.value.data!.product = undefined
    }
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
